import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import ReportingPageLayout from './ReportingPageLayout';
import KpiCardGrid from './KpiCardGrid';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import TableSettings from '../../shared/components/TableSettings';
import useReportingPage from '../hooks/useReportingPage';
import { buildStatusFilterGroup } from '../constants/eventsFiltersConfig';
import { currencyFormatter, getGranularity } from '../utils/reportingUtils';

const axios = require('axios').default;

const FINANCES_COLUMNS = [
  { id: 'event_date', label: 'Event Date', sortable: true, sortType: 'date', align: 'left', first: true, alwaysVisible: true },
  { id: 'title', label: 'Event Name', sortable: true, sortType: 'text', align: 'left', alwaysVisible: true },
  { id: 'id', label: 'Event ID', sortable: true, sortType: 'number', align: 'right' },
  { id: 'venue_name', label: 'Venue', sortable: true, sortType: 'text', align: 'left' },
  { id: 'presenter', label: 'Presenter', sortable: true, sortType: 'text', align: 'left', defaultHidden: true },
  { id: 'income', label: 'Income', sortable: true, sortType: 'number', align: 'right' },
  { id: 'expenses', label: 'Expenses', sortable: true, sortType: 'number', align: 'right' },
  { id: 'profit_loss', label: 'Profit / Loss', sortable: true, sortType: 'number', align: 'right' },
  { id: 'margin', label: 'Margin', sortable: true, sortType: 'number', align: 'right' },
  { id: 'breakeven', label: 'Breakeven', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
];

const getDefaultVisibleColumns = () => FINANCES_COLUMNS.filter(c => !c.defaultHidden).map(c => ({ id: c.id, label: c.label, alwaysVisible: c.alwaysVisible }));
const getDefaultHiddenColumns = () => FINANCES_COLUMNS.filter(c => c.defaultHidden).map(c => ({ id: c.id, label: c.label }));

const SORT_TYPES = {
  text: { asc: 'A \u2192 Z', desc: 'Z \u2192 A' },
  number: { asc: 'Low \u2192 High', desc: 'High \u2192 Low' },
  date: { asc: 'Oldest first', desc: 'Newest first' },
};

const formatEventDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

function ReportingFinancesPage({ team, csrfToken, currentUser, venueOwnerships }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters, comparison,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('year_to_date');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [sortRules, setSortRules] = useState([{ field: 'event_date', direction: 'desc' }]);
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(getDefaultVisibleColumns());
  const [hiddenColumns, setHiddenColumns] = useState(getDefaultHiddenColumns());
  const [page, setPage] = useState(1);
  const perPage = 25;

  const filterGroups = useMemo(() => {
    const groups = [];
    if (venueOwnerships && venueOwnerships.length > 0) {
      groups.push({
        id: 'venue',
        label: 'Venues',
        filters: [
          {
            id: 'venue_ownership_id',
            label: 'Venue',
            type: 'checkbox',
            options: venueOwnerships.map((vo) => ({
              value: String(vo.id),
              label: vo.name || vo.venue_name || `Venue ${vo.id}`,
            })),
          },
        ],
      });
    }
    groups.push(buildStatusFilterGroup());
    return groups;
  }, [venueOwnerships]);

  useEffect(() => {
    if (!team || !team.id) return;
    const saved = localStorage.getItem(`reportingFinancesColumns_${team.id}`);
    if (saved) {
      try {
        const { visible, hidden } = JSON.parse(saved);
        const knownIds = new Set(FINANCES_COLUMNS.map(c => c.id));
        const validVisible = visible.filter(c => knownIds.has(c.id));
        const validHidden = hidden.filter(c => knownIds.has(c.id));
        const savedIds = new Set([...validVisible.map(c => c.id), ...validHidden.map(c => c.id)]);
        const newColumns = FINANCES_COLUMNS.filter(c => !savedIds.has(c.id) && !c.alwaysVisible)
          .map(c => ({ id: c.id, label: c.label, alwaysVisible: c.alwaysVisible }));
        setVisibleColumns(validVisible);
        setHiddenColumns([...validHidden, ...newColumns]);
      } catch (e) {}
    }
  }, [team && team.id]);

  const handleColumnsChange = (newVisible, newHidden) => {
    setVisibleColumns(newVisible);
    setHiddenColumns(newHidden);
    localStorage.setItem(`reportingFinancesColumns_${team.id}`, JSON.stringify({
      visible: newVisible,
      hidden: newHidden,
    }));
  };

  const buildApiParams = useCallback(() => {
    const params = {};
    if (dateRange && dateRange.start) params.start_date = dateRange.start;
    if (dateRange && dateRange.end) params.end_date = dateRange.end;
    params.granularity = getGranularity(dateRange);
    if (comparison.start) params.prior_start_date = comparison.start;
    if (comparison.end) params.prior_end_date = comparison.end;
    if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
      params['venue_ownership_id[]'] = activeFilters.venue_ownership_id;
    }
    if (activeFilters.calendar_classification && activeFilters.calendar_classification.length > 0) {
      params['calendar_classification[]'] = activeFilters.calendar_classification;
    }
    return params;
  }, [dateRange, activeFilters, comparison]);

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    setLoading(true);
    const params = buildApiParams();
    axios.get(`/teams/${team.id}/reporting/finances/data.json`, { params })
      .then((res) => setData(res.data || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [team && team.id, csrfToken, dateRange, activeFilters]);

  const kpis = (data && data.kpis) || {};
  const prior = kpis.prior || {};
  const events = (data && data.events) || {};
  const rows = events.rows || [];
  const totals = events.totals || {};
  const charts = (data && data.charts) || {};
  const cmpLabel = comparison.label;

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      for (const rule of sortRules) {
        let av = a[rule.field];
        let bv = b[rule.field];
        if (rule.field === 'event_date') {
          av = av ? new Date(av).getTime() : 0;
          bv = bv ? new Date(bv).getTime() : 0;
        }
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av == null) av = 0;
        if (bv == null) bv = 0;
        if (av < bv) return rule.direction === 'desc' ? 1 : -1;
        if (av > bv) return rule.direction === 'desc' ? -1 : 1;
      }
      return 0;
    });
    return sorted;
  }, [rows, sortRules]);

  const filteredRows = useMemo(() => {
    if (!search) return sortedRows;
    const q = search.toLowerCase();
    return sortedRows.filter((r) =>
      (r.title || '').toLowerCase().includes(q) ||
      (r.venue_name || '').toLowerCase().includes(q) ||
      (r.presenter || '').toLowerCase().includes(q) ||
      String(r.id).includes(q)
    );
  }, [sortedRows, search]);

  useEffect(() => { setPage(1); }, [search, sortRules, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const paginatedRows = filteredRows.slice((page - 1) * perPage, page * perPage);

  const sortableColumns = FINANCES_COLUMNS.filter(c => c.sortable);

  const handleColumnSort = (colId) => {
    setSortRules(prev => {
      const existing = prev.length === 1 && prev[0].field === colId;
      if (existing) {
        return [{ field: colId, direction: prev[0].direction === 'asc' ? 'desc' : 'asc' }];
      }
      return [{ field: colId, direction: 'asc' }];
    });
  };

  const handleSortRuleChange = (index, key, value) => {
    setSortRules(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const handleAddSortRule = () => {
    const usedFields = new Set(sortRules.map(r => r.field));
    const nextCol = sortableColumns.find(c => !usedFields.has(c.id));
    if (nextCol) {
      setSortRules(prev => [...prev, { field: nextCol.id, direction: 'asc' }]);
    }
  };

  const handleRemoveSortRule = (index) => {
    setSortRules(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const sortArrow = (colId) => {
    const rule = sortRules.find(r => r.field === colId);
    if (!rule) return null;
    const index = sortRules.indexOf(rule);
    const arrow = rule.direction === 'asc' ? '\u2191' : '\u2193';
    return sortRules.length > 1 ? ` ${index + 1}${arrow}` : ` ${arrow}`;
  };

  useEffect(() => {
    if (!sortPopoverOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.reporting-sort-popover')) {
        setSortPopoverOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sortPopoverOpen]);

  const renderCellValue = (colId, row) => {
    switch (colId) {
      case 'event_date':
        return formatEventDate(row.event_date);
      case 'title':
        return <div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</div>;
      case 'id':
        return row.id;
      case 'venue_name':
        return <div style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.venue_name || '\u2014'}</div>;
      case 'presenter':
        return row.presenter || '\u2014';
      case 'income':
        return currencyFormatter.format(row.income || 0);
      case 'expenses':
        return currencyFormatter.format(row.expenses || 0);
      case 'profit_loss': {
        const val = row.profit_loss || 0;
        const color = val < 0 ? '#FF595E' : val > 0 ? '#8AC926' : 'var(--sl-text-secondary, #344054)';
        return <span style={{ color }}>{currencyFormatter.format(val)}</span>;
      }
      case 'margin':
        return row.margin != null ? `${row.margin}%` : '\u2014';
      case 'breakeven':
        return currencyFormatter.format(row.breakeven || 0);
      default:
        return '\u2014';
    }
  };

  const kpiCards = [
    { top: { label: 'Total Income', value: currencyFormatter.format(kpis.income || 0), current: kpis.income, prior: prior.income, tooltip: 'Total income across all events in the period' } },
    { top: { label: 'Total Expenses', value: currencyFormatter.format(kpis.expenses || 0), current: kpis.expenses, prior: prior.expenses, tooltip: 'Total expenses across all events in the period' } },
    { top: { label: 'Net Profit / Loss', value: currencyFormatter.format(kpis.profit_loss || 0), current: kpis.profit_loss, prior: prior.profit_loss, tooltip: 'Net profit or loss (income minus expenses)' } },
    { top: { label: 'Avg Margin', value: `${kpis.avg_margin || 0}%`, current: kpis.avg_margin, prior: prior.avg_margin, tooltip: 'Average profit margin across events (profit / income)' } },
  ];

  return (
    <ReportingPageLayout
      team={team}
      activeKey="finances"
      title="Finances"
      subtitle="Overview of your event profit and loss. Canceled events are excluded unless you select Canceled in the Status filter."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      filterTitle="Filter Finances"
      filterSubtitle="Apply filters to narrow financial data."
    >
          <KpiCardGrid
            loading={loading}
            isMobile={isMobile}
            columns={4}
            cards={kpiCards}
            comparisonLabel={cmpLabel}
            variant="centered"
          />

          {!loading && Object.keys(charts).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
              {[
                { key: 'profit_by_venue', title: 'Total Profit by Venue', dataKey: 'profit', nameKey: 'name' },
                { key: 'avg_profit_by_venue', title: 'Avg Profit by Venue', dataKey: 'avg_profit', nameKey: 'name' },
                { key: 'profit_by_buyer', title: 'Total Profit by Talent Buyer', dataKey: 'profit', nameKey: 'name' },
                { key: 'avg_profit_by_buyer', title: 'Avg Profit by Talent Buyer', dataKey: 'avg_profit', nameKey: 'name' },
                { key: 'profit_by_promoter', title: 'Total Profit by Promoter', dataKey: 'profit', nameKey: 'name' },
                { key: 'avg_profit_by_promoter', title: 'Avg Profit by Promoter', dataKey: 'avg_profit', nameKey: 'name' },
                { key: 'profit_by_event', title: 'Top 10 Events by Profit', dataKey: 'profit', nameKey: 'name' },
                { key: 'profit_by_day_of_week', title: 'Profit by Day of Week', dataKey: 'profit', nameKey: 'day' },
              ].map(({ key, title, dataKey, nameKey }) => {
                const chartData = charts[key];
                if (!chartData || chartData.length === 0) return null;
                return (
                  <div key={key} style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: 24 }}>
                    <h6 style={{ fontWeight: 700, fontSize: 14, margin: '0 0 16px', color: 'var(--sl-text-primary, #101828)' }}>{title}</h6>
                    <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 40 + 40)}>
                      <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: 'var(--sl-text-tertiary, #667085)' }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => currencyFormatter.format(v)}
                        />
                        <YAxis
                          dataKey={nameKey}
                          type="category"
                          tick={{ fontSize: 11, fill: 'var(--sl-text-tertiary, #667085)' }}
                          axisLine={false}
                          tickLine={false}
                          width={120}
                        />
                        <Tooltip
                          cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                          formatter={(value) => currencyFormatter.format(value)}
                        />
                        <Bar dataKey={dataKey} fill="#1982C4" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8 }}>
            <div className="d-flex align-items-center justify-content-between" style={{ padding: '16px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
              <h6 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: 'var(--sl-text-primary, #101828)' }}>P&L by Event</h6>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <button
                  className="btn btn-sm d-flex align-items-center"
                  style={{ gap: 6, fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  onClick={() => {
                    const header = visibleColumns.map(vc => {
                      const col = FINANCES_COLUMNS.find(c => c.id === vc.id);
                      return col ? col.label : vc.id;
                    });
                    const csvRows = sortedRows.map((r) => visibleColumns.map(vc => {
                      switch (vc.id) {
                        case 'event_date': return r.event_date ? new Date(r.event_date).toLocaleDateString() : '';
                        case 'title': return r.title || '';
                        case 'id': return r.id;
                        case 'venue_name': return r.venue_name || '';
                        case 'presenter': return r.presenter || '';
                        case 'income': return r.income || 0;
                        case 'expenses': return r.expenses || 0;
                        case 'profit_loss': return r.profit_loss || 0;
                        case 'margin': return r.margin != null ? r.margin : '';
                        case 'breakeven': return r.breakeven || 0;
                        default: return '';
                      }
                    }));
                    const csv = [header, ...csvRows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'finances_by_event.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Export CSV <i className="fas fa-download" style={{ fontSize: 12 }} />
                </button>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                    style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  >
                    <i className="fas fa-columns mr-2" style={{ fontSize: 12 }} />
                    Table Settings
                    {hiddenColumns.length > 0 && (
                      <span className="ml-1" style={{ background: 'var(--sl-fg-brand, #1982c4)', color: 'var(--sl-text-white, #fff)', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 600, lineHeight: '18px' }}>
                        {hiddenColumns.length}
                      </span>
                    )}
                  </button>
                  <TableSettings
                    isOpen={tableSettingsOpen}
                    onClose={() => setTableSettingsOpen(false)}
                    visibleFields={visibleColumns}
                    hiddenFields={hiddenColumns}
                    onFieldsChange={handleColumnsChange}
                  />
                </div>
                <div className="reporting-sort-popover" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setSortPopoverOpen(!sortPopoverOpen)}
                    style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  >
                    <i className="fas fa-sort mr-2" style={{ fontSize: 12 }} />
                    Sort{sortRules.length > 1 ? ` (${sortRules.length})` : ''}
                  </button>
                  {sortPopoverOpen && (
                    <div
                      className="card shadow-sm"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        left: 'auto',
                        zIndex: 1050,
                        minWidth: isMobile ? 280 : 420,
                        marginTop: 4,
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="font-weight-bold">Sort by</span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-muted"
                            onClick={() => setSortPopoverOpen(false)}
                          >
                            <i className="fal fa-times" />
                          </button>
                        </div>
                        {sortRules.map((rule, index) => {
                          const col = FINANCES_COLUMNS.find(c => c.id === rule.field);
                          const sortType = col?.sortType || 'text';
                          const dirLabels = SORT_TYPES[sortType] || SORT_TYPES.text;
                          return (
                            <div key={index} className="d-flex align-items-center gap-2 mb-2">
                              <select
                                className="form-control form-control-sm"
                                style={{ flex: '1 1 0' }}
                                value={rule.field}
                                onChange={(e) => handleSortRuleChange(index, 'field', e.target.value)}
                              >
                                {sortableColumns.map(c => (
                                  <option
                                    key={c.id}
                                    value={c.id}
                                    disabled={sortRules.some((r, i) => i !== index && r.field === c.id)}
                                  >
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="form-control form-control-sm"
                                style={{ flex: '1 1 0' }}
                                value={rule.direction}
                                onChange={(e) => handleSortRuleChange(index, 'direction', e.target.value)}
                              >
                                <option value="asc">{dirLabels.asc}</option>
                                <option value="desc">{dirLabels.desc}</option>
                              </select>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-muted"
                                onClick={() => handleRemoveSortRule(index)}
                                disabled={sortRules.length <= 1}
                                style={{ opacity: sortRules.length <= 1 ? 0.3 : 1 }}
                              >
                                <i className="fal fa-times" />
                              </button>
                            </div>
                          );
                        })}
                        {sortRules.length < sortableColumns.length && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-primary"
                            onClick={handleAddSortRule}
                          >
                            <i className="fal fa-plus mr-1" />
                            Add another sort
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
              <div style={{ position: 'relative', maxWidth: 400 }}>
                <i className="fas fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sl-text-disabled, #98a2b3)', fontSize: 14 }} />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search events..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 36, border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, height: 40 }}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    {visibleColumns.map((vc) => {
                      const col = FINANCES_COLUMNS.find(c => c.id === vc.id);
                      if (!col) return null;
                      return (
                        <th
                          key={col.id}
                          style={{
                            fontWeight: 500,
                            fontSize: 12,
                            color: 'var(--sl-text-tertiary, #667085)',
                            textTransform: 'uppercase',
                            textAlign: col.align,
                            ...(col.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
                            whiteSpace: 'nowrap',
                            ...(col.first ? { paddingLeft: 24 } : {}),
                          }}
                          onClick={col.sortable ? () => handleColumnSort(col.id) : undefined}
                        >
                          {col.label}{sortArrow(col.id)}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="text-center py-4 text-muted">Loading...</td>
                    </tr>
                  ) : paginatedRows.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="text-center py-4 text-muted">{search ? 'No events match your search.' : 'No financial data for the selected period.'}</td>
                    </tr>
                  ) : paginatedRows.map((row) => (
                    <tr key={row.id}>
                      {visibleColumns.map((vc) => {
                        const col = FINANCES_COLUMNS.find(c => c.id === vc.id);
                        if (!col) return null;
                        return (
                          <td key={col.id} style={{
                            fontSize: 14,
                            color: col.id === 'title' ? 'var(--sl-text-primary, #101828)' : 'var(--sl-text-secondary, #344054)',
                            fontWeight: col.id === 'title' ? 600 : undefined,
                            textAlign: col.align,
                            whiteSpace: col.id === 'event_date' ? 'nowrap' : undefined,
                            ...(col.first ? { paddingLeft: 24 } : {}),
                          }}>
                            {renderCellValue(col.id, row)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                {filteredRows.length > 0 && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--sl-border-secondary, #e4e7ec)' }}>
                      {visibleColumns.map((vc, i) => {
                        const col = FINANCES_COLUMNS.find(c => c.id === vc.id);
                        if (!col) return null;
                        const totalMap = {
                          income: currencyFormatter.format(totals.income || 0),
                          expenses: currencyFormatter.format(totals.expenses || 0),
                          profit_loss: (() => {
                            const val = totals.profit_loss || 0;
                            const color = val < 0 ? '#FF595E' : val > 0 ? '#8AC926' : 'var(--sl-text-primary, #101828)';
                            return <span style={{ color }}>{currencyFormatter.format(val)}</span>;
                          })(),
                          margin: `${totals.margin || 0}%`,
                        };
                        if (totalMap[col.id]) {
                          return (
                            <td key={col.id} style={{ textAlign: 'right', fontWeight: 700, color: 'var(--sl-text-primary, #101828)' }}>
                              {totalMap[col.id]}
                            </td>
                          );
                        }
                        const isLastNonTotal = !visibleColumns.slice(i + 1).some(v => totalMap[v.id]);
                        if (isLastNonTotal) {
                          return (
                            <td key={col.id} colSpan={1} style={{ paddingLeft: col.first ? 24 : undefined, fontWeight: 700, color: 'var(--sl-text-primary, #101828)', textAlign: 'right' }}>
                              Grand total
                            </td>
                          );
                        }
                        return <td key={col.id} />;
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
                <TeamPagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={filteredRows.length}
                  pageSize={perPage}
                  itemLabel="events"
                  onPageChange={(p) => {
                    if (p >= 1 && p <= totalPages) setPage(p);
                  }}
                />
              </div>
            )}
          </div>

    </ReportingPageLayout>
  );
}

export default ReportingFinancesPage;
